import {
  Box,
  Chip,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { useStore } from "../../hooks";
import { ISellerToken } from "../../store/seller-tokens-store";
import { clearEmptyFieldiInObject } from "../../utils/utils";
import CustomChip from "../custom-chip";
import { Delete2Icon, EditIcon, Key01Icon } from "../icons";
import Loader from "../loader/loader";
import Table, { Cell } from "../table/table";
import StyledIconButton from "../ui/StyledIconButton";
import AddFieldForm from "./components/add-field-form";
import SellerTokenData from "./components/seller-token-data";
import locale from "./locale";
import styles from "./styles";

interface ISellerTokensProps {
  showAddForm: boolean;
  setShowAddForm: (showAddForm: boolean) => void;
}

const SellerTokens: FC<ISellerTokensProps> = ({ showAddForm, setShowAddForm }) => {
  const {
    userStore: { userInfo },
    sellerTokensStore: {
      fetchSellerTokens,
      addSellerToken,
      editSellerToken,
      deleteSellerToken,
      sellerTokens,
      isLoading,
    },
    popupStore,
  } = useStore();
  const [editData, setEditData] = useState<ISellerToken | undefined>();

  const headerCells = useMemo(() => locale.headerCells, []);

  const onAdd = useCallback(
    async (data: ISellerToken) => {
      const filteredData = clearEmptyFieldiInObject<ISellerToken>(data);

      if (Boolean(editData)) {
        await editSellerToken(filteredData);
      } else {
        await addSellerToken(filteredData);
      }

      setEditData(undefined);
      setShowAddForm(false);
    },
    [editData, editSellerToken, addSellerToken, setShowAddForm]
  );

  const onCancelAdd = useCallback(() => {
    setEditData(undefined);
    setShowAddForm(false);
  }, [setShowAddForm]);

  const onRemoveField = useCallback(async (data: ISellerToken) => {
    if (data.id) {
      await deleteSellerToken(data.id, data.marketplaceType);
    }
  }, [deleteSellerToken]);

  const onEditField = useCallback((id: number) => {
    const editToken = sellerTokens?.find((token) => token.id === id);
    setEditData(editToken);
    setShowAddForm(true);
  }, [sellerTokens, setEditData, setShowAddForm]);

  const onShowTokens = useCallback((data: ISellerToken) => {
    popupStore.openPopup({
      props: { size: "sm", title: "Seller Token" },
      contentProps: { data },
      content: SellerTokenData,
    });
  }, [popupStore]);

  const renderHeaderCell = useCallback(
    (cell: Cell) => (
      <TableCell variant="footer" key={cell.name} align="left" width={cell.width}>
        {cell.name}
      </TableCell>
    ),
    []
  );

  const renderRow = useCallback(
    (field: ISellerToken, index: number) => {
      return (
        <TableRow
          key={index}
          sx={{
            backgroundColor: field.isExpired ? "#f5d5d3" : undefined,
          }}
        >
          <TableCell align="left">{field.marketplaceType}</TableCell>
          <TableCell component="th" scope="row">
            {field.name}
          </TableCell>
          <TableCell align="left">{field.currency}</TableCell>
          <TableCell align="left">{<CustomChip value={field.vat} variant="success" />}</TableCell>
          <TableCell align="left">{<CustomChip value={field.taxRate} />}</TableCell>
          <TableCell>{field.description || ""}</TableCell>
          <TableCell align="left">
            <Stack direction="row" gap={2} alignItems="center">
              <Typography>{field.expiryDate || ""}</Typography>
              {field.isExpired && (
                <Chip variant="filled" label="Истекает" color="error" />
              )}
            </Stack>
          </TableCell>
          {field.id && (
            <TableCell align="left">
              <Stack direction="row" gap={1}>
                <StyledIconButton size="small" onClick={() => onShowTokens(field)}>
                  <Key01Icon color="#56AA82" />
                </StyledIconButton>
                <StyledIconButton size="small" onClick={() => onEditField(field.id!)}>
                  <EditIcon />
                </StyledIconButton>
                <StyledIconButton size="small" onClick={() => onRemoveField(field)}>
                  <Delete2Icon />
                </StyledIconButton>
              </Stack>

            </TableCell>
          )}
        </TableRow>
      );
    },
    [onShowTokens, onEditField, onRemoveField]
  );

  useEffect(() => {
    if (!sellerTokens) {
      fetchSellerTokens();
    }
  }, [sellerTokens, fetchSellerTokens]);


  useEffect(() => {
    if (showAddForm) {
      popupStore.openPopup({
        props: {
          size: "sm",
          onClose: onCancelAdd,
          title: !!editData ? "Редактировать токен" : "Добавить новый токен"
        },
        contentProps: {
          onAdd,
          onCancel: onCancelAdd,
          defaultData: editData
        },
        content: AddFieldForm,
      });
    } else {
      popupStore.closePopup();
    }
  }, [onCancelAdd, editData, onAdd, showAddForm, popupStore]);

  return (
    <Box sx={styles.wrapper}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">{userInfo?.fio}</Typography>
      </Stack>

      {!sellerTokens?.length && !isLoading && (
        <Typography variant="caption" mt={4}>
          У вас пока нет токенов. Нажмите кнопку добавить и заполните поля для
          создания вашего первого токена
        </Typography>
      )}
      {Boolean(sellerTokens?.length) && (
        <Table
          headerCells={headerCells}
          fields={sellerTokens as ISellerToken[]}
          renderHeaderCell={renderHeaderCell}
          renderRow={renderRow}
        />
      )}

      <Loader isLoading={isLoading} />
    </Box>
  );
};

export default observer(SellerTokens);
